import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, Loader2, Clock, AlertCircle } from 'lucide-react';
import useApi from '@src/api_service/useApi';
import { PublishRequestStatusResponse, JobState } from './interface';
import { cn } from '@src/utils/app_functions';

interface RequestJobProgressViewProps {
    jobId?: string;
    onGoBack?: () => void;
    onComplete?: () => void;
}

const RequestJobProgressView: React.FC<RequestJobProgressViewProps> = ({ 
    jobId: propJobId, 
    onGoBack,
    onComplete 
}) => {
    const params = useParams<{ jobId?: string }>();
    const navigate = useNavigate();
    
    // Use jobId from props if provided, otherwise get from route params
    const jobId = propJobId || params.jobId;
    
    const [status, setStatus] = useState<PublishRequestStatusResponse | null>(null);
    const [isPolling, setIsPolling] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { loading, get } = useApi();
    
    // Default onGoBack handler if not provided
    const handleGoBack = () => {
        if (onGoBack) {
            onGoBack();
        } else {
            navigate('/home/third-party-requests');
        }
    };

    const fetchStatus = useCallback(async () => {
        const token = localStorage.getItem('token');
        
        await get<PublishRequestStatusResponse, any>(
            `/get_publish_request_status/${jobId}`,
            { Authorization: `Bearer ${token}` },
            {
                loading: false,
                onSuccess: (response) => {
                    setStatus(response);
                    
                    if (response.data?.state === 'completed') {
                        setIsPolling(false);
                        if (onComplete) {
                            setTimeout(() => onComplete(), 2000);
                        }
                    } else if (response.data?.state === 'failed') {
                        setIsPolling(false);
                        setError(response.data.error || 'Job failed');
                    }
                },
                onError: (err) => {
                    console.error('Error fetching job status:', err);
                    setError('Failed to fetch job status');
                    setIsPolling(false);
                }
            }
        );
    }, [jobId, get, onComplete]);

    useEffect(() => {
        if (!isPolling) return;

        fetchStatus();
        const interval = setInterval(fetchStatus, 10000);

        return () => clearInterval(interval);
    }, [isPolling, fetchStatus]);

    const getStateIcon = (state: JobState) => {
        switch (state) {
            case 'completed':
                return <CheckCircle className="w-6 h-6 text-green-600" />;
            case 'failed':
                return <XCircle className="w-6 h-6 text-red-600" />;
            case 'active':
                return <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />;
            case 'waiting':
            case 'delayed':
                return <Clock className="w-6 h-6 text-yellow-600" />;
            default:
                return <AlertCircle className="w-6 h-6 text-gray-600" />;
        }
    };

    const getStateBadgeClass = (state: JobState) => {
        switch (state) {
            case 'completed':
                return 'bg-green-100 text-green-800';
            case 'failed':
                return 'bg-red-100 text-red-800';
            case 'active':
                return 'bg-blue-100 text-blue-800';
            case 'waiting':
            case 'delayed':
                return 'bg-yellow-100 text-yellow-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading && !status) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                    <span className="text-gray-600">Loading job status...</span>
                </div>
            </div>
        );
    }

    if (!jobId) {
        return (
            <div className="max-w-4xl mx-auto p-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <h3 className="text-yellow-800 font-semibold mb-2">Missing Job ID</h3>
                            <p className="text-yellow-600">No job ID provided. Please check the URL or try again.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error && !status?.data) {
        return (
            <div className="max-w-4xl mx-auto p-6">
                <button
                    onClick={handleGoBack}
                    className="flex items-center gap-2 mb-6 text-gray-900 transition-colors hover:text-gray-700"
                >
                    <ArrowLeft className="w-5 h-5" />
                    <span className="font-medium">Go Back</span>
                </button>
                
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <div className="flex items-start gap-3">
                        <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <h3 className="text-red-800 font-semibold mb-2">Error</h3>
                            <p className="text-red-600">{error}</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!status?.data) {
        return null;
    }

    const { state, progress, logs, data, result, attemptsMade, processedOn, finishedOn } = status.data;

    return (
        <div className="max-w-4xl mx-auto p-6">
            <button
                onClick={handleGoBack}
                className="flex items-center gap-2 mb-6 text-gray-900 transition-colors hover:text-gray-700"
            >
                <ArrowLeft className="w-5 h-5" />
                <span className="font-medium">Go Back</span>
            </button>

            <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                        <h2 className="text-2xl font-bold">Publish Request Status</h2>
                        {getStateIcon(state)}
                    </div>
                    <p className="text-blue-100 text-sm">Job ID: {jobId}</p>
                </div>

                <div className="p-6">
                    {/* Status Badge */}
                    <div className="mb-6">
                        <span className={cn(
                            'inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold',
                            getStateBadgeClass(state)
                        )}>
                            {state.charAt(0).toUpperCase() + state.slice(1)}
                        </span>
                    </div>

                    {/* Progress Bar */}
                    {state !== 'completed' && state !== 'failed' && (
                        <div className="mb-8">
                            <div className="flex justify-between mb-2">
                                <span className="text-sm font-medium text-gray-700">Progress</span>
                                <span className="text-sm font-medium text-gray-700">{progress}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                                <div 
                                    className="bg-blue-600 h-3 rounded-full transition-all duration-500 ease-out"
                                    style={{ width: `${progress}%` }}
                                ></div>
                            </div>
                        </div>
                    )}

                    {/* Success Result */}
                    {result && state === 'completed' && (
                        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                                <div>
                                    <h4 className="text-green-800 font-semibold mb-1">Success</h4>
                                    <p className="text-green-700 text-sm">{result.message}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Error Message */}
                    {status.data.error && state === 'failed' && (
                        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                <div>
                                    <h4 className="text-red-800 font-semibold mb-1">Error</h4>
                                    <p className="text-red-700 text-sm">{status.data.error}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Job Details */}
                    <div className="bg-gray-50 rounded-lg p-5 mb-6">
                        <h3 className="text-sm font-semibold text-gray-700 mb-4">Request Details</h3>
                        <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                                <dt className="text-gray-600 mb-1">Domain</dt>
                                <dd className="text-gray-900 font-medium">{data.domain}</dd>
                            </div>
                            
                            <div>
                                <dt className="text-gray-600 mb-1">Status</dt>
                                <dd className="text-gray-900 font-medium capitalize">{data.status}</dd>
                            </div>
                            
                            <div>
                                <dt className="text-gray-600 mb-1">App ID</dt>
                                <dd className="text-gray-900 font-medium">{data.appID}</dd>
                            </div>

                            <div>
                                <dt className="text-gray-600 mb-1">Attempts</dt>
                                <dd className="text-gray-900 font-medium">{attemptsMade}</dd>
                            </div>

                            {processedOn && (
                                <div>
                                    <dt className="text-gray-600 mb-1">Started At</dt>
                                    <dd className="text-gray-900 font-medium">
                                        {new Date(processedOn).toLocaleString()}
                                    </dd>
                                </div>
                            )}

                            {finishedOn && (
                                <div>
                                    <dt className="text-gray-600 mb-1">Finished At</dt>
                                    <dd className="text-gray-900 font-medium">
                                        {new Date(finishedOn).toLocaleString()}
                                    </dd>
                                </div>
                            )}
                        </dl>
                    </div>

                    {/* Logs */}
                    {logs.length > 0 && (
                        <div className="bg-gray-900 rounded-lg p-5">
                            <h3 className="text-white text-sm font-semibold mb-3 flex items-center gap-2">
                                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                                Execution Logs
                            </h3>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                {logs.map((log, index) => (
                                    <div key={index} className="text-gray-300 text-xs font-mono flex items-start gap-2">
                                        <span className="text-gray-500 flex-shrink-0">[{index + 1}]</span>
                                        <span>{log}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Polling Indicator */}
                    {isPolling && state !== 'completed' && state !== 'failed' && (
                        <div className="mt-6 flex items-center justify-center text-sm text-gray-600">
                            <div className="flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-full">
                                <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                                <span>Checking for updates...</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RequestJobProgressView;